import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CheckCircle2, AlertCircle, AlertTriangle, Info } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { CookieConsent } from "./components/CookieConsent";
import Legal, { NotFound, ErrorFallback } from "./pages/Legal";
import React from "react";
import { MobileBottomNav } from "./components/MobileBottomNav";
import Help from "./pages/Help";
import Home from "./pages/Home";
import Auth, { ForgotPassword, ResetPassword, VerifyEmail, AuthCallback } from "./pages/Auth";
import SearchPage from "./pages/Search";
import ItemDetail from "./pages/ItemDetail";
import Sell from "./pages/Sell";
import Checkout, { PaymentSuccess, PaymentCancel } from "./pages/Checkout";
import Messages from "./pages/Messages";
import Orders from "./pages/Orders";
import SellerDashboard from "./pages/SellerDashboard";
import Geldbeutel from "./pages/Geldbeutel";
import SellerProfile from "./pages/SellerProfile";
import Admin from "./pages/Admin";
import { Favorites, Notifications } from "./pages/Account";
import Settings from "./pages/Settings";
import Werbung, { AdsSuccess } from "./pages/Werbung";
import Gewinnspiel from "./pages/Gewinnspiel";
import MeineAnfragen from "./pages/MeineAnfragen";
import { HelpArticle } from "./pages/Help";
import { Seo } from "./components/Seo";

const lazyInfo = (name) => React.lazy(() => import("./pages/info").then((m) => ({ default: m[name] })));
const INFO_ROUTES = [
  [["/ueber-uns", "/about"], "About"],
  [["/so-funktionierts", "/how-it-works"], "HowItWorks"],
  [["/kaeuferschutz", "/buyer-protection"], "BuyerProtection"],
  [["/rueckerstattungen", "/refunds"], "Refunds"],
  [["/gebuehren", "/fees"], "Fees"],
  [["/sicherheit", "/safety"], "Safety"],
  [["/meldestelle", "/report"], "Report"],
  [["/markenschutz", "/brand-protection"], "BrandProtection"],
  [["/faq"], "FAQPage"],
  [["/verkaufen-auf-kledio", "/sell-on-kledio"], "SellGuide"],
  [["/badges", "/seller-badges"], "Badges"],
  [["/auszahlungen", "/payouts"], "Payouts"],
  [["/agb", "/terms"], "Terms"],
  [["/datenschutz", "/privacy"], "Privacy"],
  [["/impressum"], "Imprint"],
  [["/verkaeuferbedingungen", "/seller-terms"], "SellerTerms"],
  [["/kontakt", "/contact"], "Contact"],
  [["/barrierefreiheit", "/accessibility"], "Accessibility"],
  [["/nachhaltigkeit", "/sustainability"], "Sustainability"],
  [["/neu", "/new"], "NewItems"],
  [["/beliebt", "/popular"], "PopularItems"],
  [["/kategorien", "/categories"], "CategoriesPage"],
];
const infoComponents = Object.fromEntries(INFO_ROUTES.map(([, name]) => [name, lazyInfo(name)]));

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function HashGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

function Protected({ children, staff }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/anmelden" replace />;
  if (staff && !["SUPPORT", "MODERATOR", "ADMIN", "SUPER_ADMIN"].includes(user.role)) return <Navigate to="/" replace />;
  return <><Seo noindex /> {children}</>;
}

function Footer_OLD() {
  return null;
}

class ErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) { console.error("[KLEDIO ErrorBoundary]", err, info); }
  render() {
    if (this.state.hasError) return <ErrorFallback onRetry={() => { this.setState({ hasError: false }); window.location.reload(); }} />;
    return this.props.children;
  }
}

function SiteChrome({ children }) {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return children;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SiteChrome><Header /></SiteChrome>
          <main className="min-h-[70vh]">
            <React.Suspense fallback={<div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
            <HashGate>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/hilfe" element={<Help />} />
              <Route path="/help" element={<Help />} />
              <Route path="/hilfe/artikel/:slug" element={<HelpArticle />} />
              {INFO_ROUTES.flatMap(([paths, name]) => {
                const C = infoComponents[name];
                return paths.map((p) => <Route key={p} path={p} element={<C />} />);
              })}
              <Route path="/anmelden" element={<Auth mode="login" />} />
              <Route path="/login" element={<Auth mode="login" />} />
              <Route path="/registrieren" element={<Auth mode="register" />} />
              <Route path="/register" element={<Auth mode="register" />} />
              <Route path="/passwort-vergessen" element={<ForgotPassword />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/suche" element={<SearchPage />} />
              <Route path="/artikel/:id" element={<ItemDetail />} />
              <Route path="/profil/:userId" element={<SellerProfile />} />
              <Route path="/verkaufen" element={<Protected><Sell /></Protected>} />
              <Route path="/kaufen/:itemId" element={<Checkout />} />
              <Route path="/zahlung/erfolg" element={<PaymentSuccess />} />
              <Route path="/zahlung/abbruch" element={<PaymentCancel />} />
              <Route path="/nachrichten" element={<Protected><Messages /></Protected>} />
              <Route path="/bestellungen" element={<Protected><Orders /></Protected>} />
              <Route path="/verkaeufer" element={<Protected><SellerDashboard /></Protected>} />
              <Route path="/geldbeutel" element={<Protected><Geldbeutel /></Protected>} />
              <Route path="/favoriten" element={<Protected><Favorites /></Protected>} />
              <Route path="/benachrichtigungen" element={<Protected><Notifications /></Protected>} />
              <Route path="/widerruf" element={<Navigate to="/agb" replace />} />
              <Route path="/konto" element={<Protected><Settings /></Protected>} />
              <Route path="/konto/:section" element={<Protected><Settings /></Protected>} />
              <Route path="/werbung" element={<Protected><Werbung /></Protected>} />
              <Route path="/werbung/erfolg" element={<Protected><AdsSuccess /></Protected>} />
              <Route path="/gewinnspiel" element={<Gewinnspiel />} />
              <Route path="/anfragen" element={<Protected><MeineAnfragen /></Protected>} />
              <Route path="/admin" element={<Protected staff><Admin /></Protected>} />
              <Route path="/:slug" element={<Legal />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </HashGate>
            </React.Suspense>
          </main>
          <SiteChrome><Footer /></SiteChrome>
          <SiteChrome><MobileBottomNav /></SiteChrome>
          <SiteChrome><CookieConsent /></SiteChrome>
          <Toaster position="top-right" duration={3000} gap={10}
            icons={{
              success: <span className="w-5 h-5 rounded-full bg-accent text-white flex items-center justify-center"><CheckCircle2 className="w-3.5 h-3.5" /></span>,
              error: <span className="w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center"><AlertCircle className="w-3.5 h-3.5" /></span>,
              warning: <AlertTriangle className="w-4 h-4 text-amber-500" />,
              info: <Info className="w-4 h-4 text-foreground" />,
            }}
            toastOptions={{
              unstyled: true,
              classNames: {
                toast: "bg-white border border-border rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] p-4 flex items-center gap-3 w-[360px] max-w-[calc(100vw-24px)] text-sm font-medium text-foreground",
                title: "text-sm font-semibold text-foreground",
                description: "text-xs text-muted-foreground",
              },
            }} />
        </BrowserRouter>
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
